import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { Target, Clock, Users, HelpCircle, ChevronDown, Lightbulb } from "lucide-react";

interface SessionContext {
  decision_type?: string;
  decision_subtype?: string;
  trigger?: string;
  stakes?: string;
}

interface Phase11StructuredInputProps {
  onValueChange: (value: string) => void;
  onStructuredChange?: (data: StructuredInputData) => void;
  sessionContext?: SessionContext;
  className?: string;
}

export interface StructuredInputData {
  signal: string;
  timeframe: string;
  customTimeframe?: string;
  validators: string[];
  additionalNotes?: string;
}

const timeframeOptions = [
  { value: "7", label: "7 Tage" },
  { value: "30", label: "30 Tage" },
  { value: "90", label: "90 Tage" },
  { value: "custom", label: "Benutzerdefiniert" },
];

// Hierarchisiert: Härteste Validierung zuerst
const validatorOptions = [
  { id: "data", label: "Daten / Metrik", hint: null },
  { id: "customer", label: "Kunden", hint: null },
  { id: "board", label: "Entscheider / Board", hint: null },
  { id: "team", label: "Internes Team", sublabel: "(kein Marktsignal)", hint: "Internes Feedback allein zählt nicht als Validierung einer Marktentscheidung." },
];

// Strukturiertes Satzmuster als Placeholder - mit explizitem Entscheidungstyp
const getSignalPlaceholder = (): string => {
  return "z.B. Mindestens 2 Kunden treffen innerhalb von 30 Tagen eine dokumentierte Go/No-Go-Entscheidung";
};

// Timeframe-Hinweise für kalibrierte Reibung - verschärft
const getTimeframeHint = (value: string): string | null => {
  if (value === "7") {
    return "Kurzfristige Zeiträume erfordern ein klares Commitment-Signal, kein Meinungsfeedback.";
  }
  return null;
};

export const Phase11StructuredInput = ({ 
  onValueChange, 
  onStructuredChange,
  sessionContext,
  className 
}: Phase11StructuredInputProps) => {
  const [signal, setSignal] = useState("");
  const [timeframe, setTimeframe] = useState("");
  const [customTimeframe, setCustomTimeframe] = useState("");
  const [validators, setValidators] = useState<string[]>([]);
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);

  // Combine all inputs into a structured answer
  const updateCombinedValue = (
    newSignal: string,
    newTimeframe: string,
    newCustomTimeframe: string,
    newValidators: string[],
    newNotes: string
  ) => {
    const parts: string[] = [];
    
    if (newSignal.trim()) {
      parts.push(`Signal: ${newSignal.trim()}`);
    }
    
    const timeValue = newTimeframe === "custom" && newCustomTimeframe 
      ? newCustomTimeframe 
      : newTimeframe === "custom" 
        ? "" 
        : newTimeframe ? `${newTimeframe} Tage` : "";
    
    if (timeValue) {
      parts.push(`Zeitrahmen: ${timeValue}`);
    }
    
    if (newValidators.length > 0) {
      const validatorLabels = newValidators.map(v => 
        validatorOptions.find(opt => opt.id === v)?.label || v
      );
      parts.push(`Validierung durch: ${validatorLabels.join(", ")}`);
    }
    
    if (newNotes.trim()) {
      parts.push(`Zusatz: ${newNotes.trim()}`);
    }
    
    onValueChange(parts.join(" | "));
    
    // Also emit structured data for meta storage
    if (onStructuredChange) {
      onStructuredChange({
        signal: newSignal.trim(),
        timeframe: timeValue,
        customTimeframe: newCustomTimeframe,
        validators: newValidators,
        additionalNotes: newNotes.trim() || undefined,
      });
    }
  };

  const handleSignalChange = (value: string) => {
    setSignal(value);
    updateCombinedValue(value, timeframe, customTimeframe, validators, additionalNotes);
  };

  const handleTimeframeChange = (value: string) => {
    setTimeframe(value);
    updateCombinedValue(signal, value, customTimeframe, validators, additionalNotes);
  };

  const handleCustomTimeframeChange = (value: string) => {
    setCustomTimeframe(value);
    updateCombinedValue(signal, timeframe, value, validators, additionalNotes);
  };

  const handleValidatorChange = (validatorId: string, checked: boolean) => {
    const newValidators = checked 
      ? [...validators, validatorId]
      : validators.filter(v => v !== validatorId);
    setValidators(newValidators);
    updateCombinedValue(signal, timeframe, customTimeframe, newValidators, additionalNotes);
  };

  const handleNotesChange = (value: string) => {
    setAdditionalNotes(value);
    updateCombinedValue(signal, timeframe, customTimeframe, validators, value);
  };

  // Progress calculation
  const filledCount = [
    signal.trim().length > 0,
    timeframe.length > 0 && (timeframe !== "custom" || customTimeframe.trim().length > 0),
    validators.length > 0
  ].filter(Boolean).length;

  return (
    <div className={cn("space-y-6", className)}>
      {/* Explanation Header */}
      <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Target className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-1">
              Erfolg messbar machen
            </h3>
            <p className="text-[15px] text-muted-foreground leading-relaxed">
              Definiere konkret, woran du objektiv erkennst, dass diese Entscheidung funktioniert. 
              Ein gutes Erfolgssignal ist beobachtbar, zeitlich gebunden und durch jemanden validierbar.
            </p>
          </div>
        </div>
        
        {/* Progress indicator */}
        <div className="mt-4 pt-3 border-t border-primary/10">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Fortschritt</span>
            <span className={cn(
              "font-medium",
              filledCount === 3 ? "text-emerald-600" : "text-foreground/70"
            )}>
              {filledCount} von 3 Pflichtfeldern
            </span>
          </div>
          <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
            <div 
              className={cn(
                "h-full transition-all duration-300 rounded-full",
                filledCount === 3 ? "bg-emerald-500" : "bg-primary"
              )}
              style={{ width: `${(filledCount / 3) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Block 1: Beobachtbares Signal - strukturiertes Satzmuster */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-primary" />
          <Label htmlFor="signal" className="text-base font-semibold text-foreground">
            Beobachtbares Signal
            <span className="text-destructive ml-1">*</span>
          </Label>
        </div>
        <p className="text-sm text-foreground/70 leading-relaxed">
          Vervollständige: <span className="font-medium text-foreground">„Wir wissen, dass die Entscheidung richtig war, wenn..."</span>
        </p>
        <Input
          id="signal"
          value={signal}
          onChange={(e) => handleSignalChange(e.target.value)}
          placeholder={getSignalPlaceholder()}
          className="bg-background border-border/60 focus:border-primary focus:ring-primary/20 text-[15px] h-12"
        />
        <p className="text-xs text-muted-foreground">
          Tipp: Wer tut was, bis wann? Feedback allein zählt nicht als Entscheidung.
        </p>
      </div>

      {/* Block 2: Zeitliche Bindung mit kalibrierten Hinweisen */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary" />
          <Label className="text-base font-semibold text-foreground">
            Zeitliche Bindung
            <span className="text-destructive ml-1">*</span>
          </Label>
        </div>
        <p className="text-sm text-foreground/70 leading-relaxed">
          Bis wann muss dieses Signal eintreten, damit du die Entscheidung als erfolgreich bewerten kannst?
        </p>
        <RadioGroup
          value={timeframe}
          onValueChange={handleTimeframeChange}
          className="flex flex-wrap gap-2"
        >
          {timeframeOptions.map((option) => (
            <div key={option.value} className="flex items-center">
              <RadioGroupItem
                value={option.value}
                id={`timeframe-${option.value}`}
                className="peer sr-only"
              />
              <Label
                htmlFor={`timeframe-${option.value}`}
                className={cn(
                  "px-4 py-2.5 rounded-full border-2 cursor-pointer transition-all text-sm font-medium",
                  "hover:bg-muted/50 hover:border-primary/40",
                  timeframe === option.value
                    ? "bg-primary/10 border-primary text-primary"
                    : "bg-background border-border/60 text-foreground/80"
                )}
              >
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
        
        {/* Kalibrierter Hinweis bei 7 Tagen */}
        {timeframe && getTimeframeHint(timeframe) && (
          <p className="text-xs text-amber-600 dark:text-amber-500 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-800">
            {getTimeframeHint(timeframe)}
          </p>
        )}
        
        {timeframe === "custom" && (
          <Input
            value={customTimeframe}
            onChange={(e) => handleCustomTimeframeChange(e.target.value)}
            placeholder="z.B. bis Q2 2026, bis zum nächsten Board-Meeting"
            className="mt-2 bg-background border-border/60 focus:border-primary focus:ring-primary/20 text-[15px] h-11"
          />
        )}
      </div>

      {/* Block 3: Validierung - hierarchisiert */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          <Label className="text-base font-semibold text-foreground">
            Validierung
            <span className="text-destructive ml-1">*</span>
          </Label>
        </div>
        <p className="text-sm text-foreground/70 leading-relaxed">
          Wer kann bestätigen, dass das Signal relevant und aussagekräftig ist? <span className="text-foreground/50">(Härteste zuerst)</span>
        </p>
        <div className="grid grid-cols-2 gap-3">
          {validatorOptions.map((option) => (
            <div 
              key={option.id} 
              className="flex flex-col"
            >
              <div 
                className={cn(
                  "flex items-center space-x-3 p-3 rounded-lg border-2 transition-all cursor-pointer",
                  validators.includes(option.id)
                    ? "border-primary bg-primary/5"
                    : "border-border/60 hover:border-primary/40 hover:bg-muted/30",
                  option.hint && validators.includes(option.id) && "border-amber-500 bg-amber-50/50 dark:bg-amber-950/20"
                )}
                onClick={() => handleValidatorChange(option.id, !validators.includes(option.id))}
              >
                <Checkbox
                  id={`validator-${option.id}`}
                  checked={validators.includes(option.id)}
                  onCheckedChange={(checked) => 
                    handleValidatorChange(option.id, checked as boolean)
                  }
                  className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                <Label
                  htmlFor={`validator-${option.id}`}
                  className={cn(
                    "text-sm font-medium cursor-pointer",
                    validators.includes(option.id) ? "text-foreground" : "text-foreground/80"
                  )}
                >
                  {option.label}
                  {(option as any).sublabel && (
                    <span className="text-muted-foreground font-normal ml-1">{(option as any).sublabel}</span>
                  )}
                </Label>
              </div>
              {/* Warnhinweis für schwache Validierung */}
              {option.hint && validators.includes(option.id) && (
                <p className="text-xs text-amber-600 dark:text-amber-500 mt-1.5 px-1">
                  {option.hint}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Help Section */}
      <Collapsible open={helpOpen} onOpenChange={setHelpOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full justify-center py-2">
          <HelpCircle className="w-4 h-4" />
          <span>Was macht ein gutes Erfolgssignal aus?</span>
          <ChevronDown className={cn(
            "w-4 h-4 transition-transform",
            helpOpen && "rotate-180"
          )} />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-3 p-4 bg-muted/30 border border-border/40 rounded-xl space-y-4">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Gutes Signal:</p>
                  <p className="text-sm text-foreground/80 italic">
                    "3 von 5 Kunden nutzen die Lösung eigenständig nach 14 Tagen"
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Schwaches Signal:</p>
                  <p className="text-sm text-foreground/80 italic">
                    "Das Feature kommt gut an" (nicht messbar, nicht zeitgebunden)
                  </p>
                </div>
                <p className="text-xs text-muted-foreground pt-2 border-t border-border/30">
                  Ein starkes Signal ist spezifisch, beobachtbar und lässt keinen Interpretationsspielraum.
                </p>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Block 4: Optional - Zusätzliche Hinweise */}
      <div className="space-y-2 pt-4 border-t border-border/40">
        <Label htmlFor="notes" className="text-sm font-medium text-muted-foreground">
          Zusätzliche Hinweise (optional)
        </Label>
        <Textarea
          id="notes"
          value={additionalNotes}
          onChange={(e) => handleNotesChange(e.target.value)}
          placeholder="Falls relevant: weitere Kontextinformationen oder Einschränkungen"
          className="min-h-[70px] bg-background/50 border-border/60 focus:border-primary focus:ring-primary/20 text-sm resize-none"
        />
      </div>
    </div>
  );
};
